import { useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import Image from "next/image";
import React from "react";
import { CourseAuthorIdType } from "__generated__/globalTypes";
import Container from "components/common/Container";
import ErrorPage from "components/common/ErrorPage";
import Heading from "components/common/Heading";
import HtmlContent from "components/common/HtmlContent";
import Link from "components/common/Link";
import { FullPageLoading } from "components/common/Loading";
import CourseTitle from "components/courses/CourseTitle";
import { COURSE_URL } from "constants/urls";
import { getAuthorNameWithTitle } from "helpers/courseDetails";
import { usePageMeta } from "hooks/usePageMeta";
import { useRouterQuery } from "hooks/useRouterQuery";
import {
  CourseAuthorQueryQuery as CourseAuthorQuery,
  CourseAuthorQueryVariables,
} from "./__generated__/queries";
import { COURSE_AUTHOR_QUERY } from "./queries";
import styles from "./CourseAuthor.module.css";

export type TokensParams = {
  slug: string;
  page?: string;
};

const CourseAuthor = () => {
  const {
    routerQuery: { slug },
  } = useRouterQuery<TokensParams>();

  const { data, loading } = useEnhancedQuery<CourseAuthorQuery, CourseAuthorQueryVariables>(
    COURSE_AUTHOR_QUERY,
    {
      variables: { id: slug, idType: CourseAuthorIdType.SLUG },
    }
  );

  const courseAuthor = data?.courseAuthor;

  usePageMeta(getAuthorNameWithTitle(courseAuthor), courseAuthor?.seo);

  if (loading) return <FullPageLoading />;
  if (!courseAuthor) return <ErrorPage />;

  const htmlContent = courseAuthor?.content;
  const featuredImage = courseAuthor?.featuredImage?.node;

  return (
    <Container>
      <div className={styles.mainContainer}>
        <div className={styles.authorInfoContainer}>
          <div className={styles.authorHeading}>
            {featuredImage?.mediaItemUrl && (
              <div className={styles.authorImage}>
                <Image
                  src={featuredImage.mediaItemUrl}
                  alt="author"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            )}
            <div>
              <div className={styles.authorTitle}>About the Author</div>
              <CourseTitle>{getAuthorNameWithTitle(courseAuthor)}</CourseTitle>
            </div>
          </div>
          <HtmlContent>{htmlContent}</HtmlContent>
        </div>
        <div className={styles.authorSidebar}>
          <Heading tag="h3" className="pb-2">
            For author&apos;s online courses see:
          </Heading>
          {courseAuthor.courses.filter(notEmpty).map(course => (
            <Link
              className={styles.authorSidebarLink}
              key={course.id}
              href={makeUrl(COURSE_URL, { slug: course.slug as string, page: "" })}
            >
              {course.title || ""}
            </Link>
          ))}
        </div>
      </div>
    </Container>
  );
};

export default CourseAuthor;
